// The weekly challenge on the wire (SCENARIOS Q9,
// PLAN-2026-11-01-the-weekly-challenge §5).
//
// One week of the sport, the same for everybody on the site: its drawn
// positions and categories, each position's tiers of players with the numbers
// a person weighs, my entry, and the boards. The arithmetic is `challenge.ts`
// beside it; these are its shapes.

import { z } from 'zod';
import { hasStrongLanguage } from '../chat/mask';
import { PlayerSummary, TeamRef } from './views';

const Iso = z.string();

/** One thing the week is scored on, said for somebody who has never played. */
export const ChallengeCategoryView = z.object({
  id: z.string(),
  /** 'Completion percentage' */
  name: z.string(),
  /** A column's head where room is short: 'Cmp%'. */
  short: z.string(),
  /** 'passing yards', 'yards a carry': what one of it is called. */
  unit: z.string(),
  /** How it scores, in words: "1 point for every 10", "minus 2 points each". */
  rate: z.string(),
  /** Its points for each one (or each `every`, as `rate` says), taken away when negative: 6, 1, -2. */
  points: z.number(),
  /** It takes points away (interceptions thrown). */
  against: z.boolean(),
  /** What a rate needs to count at all: "at least 10 passes thrown"; null for a count. */
  needs: z.string().nullable(),
  /** Display precision of a value. */
  decimals: z.number().int(),
});

/** A player in a tier: who he is, his game, and his numbers. */
export const ChallengePlayer = z.object({
  player: PlayerSummary,
  /** His club's opponent this week, and whether at home; null with no game found. */
  opponent: z.string().nullable(),
  home: z.boolean(),
  kickoff: Iso.nullable(),
  /** His game has started: he cannot be put in or taken out. */
  locked: z.boolean(),
  /** Fantasy points a game over the games his tier was measured on, and how many. */
  perGame: z.number(),
  games: z.number().int(),
  /**
   * His average in each of the position's categories over those games, in the
   * categories' order: a count's mean, a rate over all of them together, null
   * for a rate they never had enough of.
   */
  averages: z.array(z.number().nullable()),
  /**
   * The week, once his line is in: each category's value (null for a rate
   * without enough) and points, and his total. Null before his line is read.
   */
  week: z
    .object({
      values: z.array(z.number().nullable()),
      points: z.array(z.number()),
      total: z.number(),
    })
    .nullable(),
});
export type ChallengePlayerShape = z.infer<typeof ChallengePlayer>;

export const ChallengePositionView = z.object({
  position: z.string(),
  /** 'Quarterback' */
  name: z.string(),
  categories: z.array(ChallengeCategoryView),
  /** Best first, each tier's players by points a game. */
  tiers: z.array(z.array(ChallengePlayer)),
});

/** My entry: a pick a slot (position, then tier), null for an empty one, and a captain among them. */
export const ChallengeEntryView = z.object({
  picks: z.array(z.string().nullable()),
  captain: z.string().nullable(),
  /** Its points so far, and its captain's, which break a tie. */
  points: z.number(),
  captainPoints: z.number(),
  /** Its place among every entry, once any pick has a line; null before. */
  place: z.number().int().nullable(),
  savedAt: Iso,
});
export type ChallengeEntryShape = z.infer<typeof ChallengeEntryView>;

const ChallengeState = z.enum(['upcoming', 'open', 'playing', 'over']);

const WeekRef = z.object({
  season: z.number().int(),
  period: z.number().int(),
  label: z.string(),
  /** Where it stands now: two weeks can be live at once (B-620). */
  state: ChallengeState,
});

export const ChallengeWeekView = z.object({
  sport: z.string(),
  season: z.number().int(),
  period: z.number().int(),
  /** 'Week 9' */
  label: z.string(),
  /**
   * `upcoming` until it opens; `open` from then until its last kickoff, while
   * a pick whose game has not started can change; `playing` from its last
   * kickoff, when nothing can; `over` at its end, once its lines are in.
   */
  state: ChallengeState,
  /**
   * When it opens: the first kickoff of the week before it, so the next week
   * is open from the first lock of this one (B-620); a season's first week a
   * week before its first game (B-621).
   */
  opensAt: Iso,
  endsAt: Iso,
  /** The week's first kickoff, of any game it plays: the first lock. */
  firstKickoff: Iso.nullable(),
  /** The week's last kickoff: after it nothing can change. */
  lastKickoff: Iso.nullable(),
  /** The captain's points count this many times over. */
  captain: z.number(),
  /** In the sport's order; empty before the week opens. */
  positions: z.array(ChallengePositionView),
  /** Mine, or null with no entry. */
  mine: ChallengeEntryView.nullable(),
  /** My challenge name, which the public board shows; null before I choose one. */
  name: z.string().nullable(),
  /** How many entries the week has. */
  entries: z.number().int(),
  /**
   * Every week that has opened, oldest first, with where each stands: for a
   * stepper, and for the other week live beside this one (the one being
   * played while the next takes picks).
   */
  weeks: z.array(WeekRef),
});
export type ChallengeWeekViewShape = z.infer<typeof ChallengeWeekView>;

export const ChallengeBoardRow = z.object({
  place: z.number().int(),
  /** The challenge name; on a league's board, the team's name. */
  name: z.string(),
  /** On a league's board, the member's team. */
  team: TeamRef.nullable(),
  points: z.number(),
  captainPoints: z.number(),
  mine: z.boolean(),
});
export type ChallengeBoardRowShape = z.infer<typeof ChallengeBoardRow>;

export const ChallengeBoardView = z.object({
  sport: z.string(),
  season: z.number().int(),
  period: z.number().int(),
  label: z.string(),
  /** The league whose members these are, or null for everybody's. */
  leagueId: z.string().nullable(),
  /** Every entry of the week, the site's. */
  entries: z.number().int(),
  /**
   * Some entry has a pick whose line is in. Before then every entry has
   * nothing and a place says nothing, so a client lists who entered without
   * places or points.
   */
  scored: z.boolean(),
  /**
   * Everybody's top fifty; or every member of the league who entered, each
   * `place` among everybody.
   */
  rows: z.array(ChallengeBoardRow),
  /** My place among everybody and my points, or null with no entry. */
  me: z.object({ place: z.number().int(), points: z.number() }).nullable(),
});
export type ChallengeBoardViewShape = z.infer<typeof ChallengeBoardView>;

/** `PUT /api/sports/:sport/challenge/:season/:period`: my whole entry. */
export const SaveChallengeEntryRequest = z.object({
  picks: z.array(z.string().min(1).nullable()).max(24),
  captain: z.string().min(1).nullable(),
});
export type SaveChallengeEntryRequest = z.infer<typeof SaveChallengeEntryRequest>;

export const CHALLENGE_NAME_MAX = 24;

/**
 * Why a challenge name will not do, or null when it will: 2 to 24 letters,
 * numbers, spaces and simple punctuation, and no strong language, since the
 * public board is anybody's to read (DIRECTION A15).
 */
export function challengeNameProblem(name: string): string | null {
  const n = name.trim();
  if (n.length < 2) return 'A name is 2 characters at least.';
  if (n.length > CHALLENGE_NAME_MAX) return `A name is ${CHALLENGE_NAME_MAX} characters at most.`;
  if (!/^[\p{L}\p{N}][\p{L}\p{N} '’.\-_!&]*$/u.test(n))
    return 'Use letters, numbers, spaces and simple punctuation, starting with a letter or number.';
  if (/\s{2}/.test(n)) return 'One space between words.';
  if (hasStrongLanguage(n)) return 'Pick a name anybody can read.';
  return null;
}

/** `PUT /api/me/challenge-name`. */
export const ChallengeNameRequest = z.object({ name: z.string().max(200) });
export const ChallengeNameView = z.object({ name: z.string() });
export type ChallengeNameViewShape = z.infer<typeof ChallengeNameView>;
